import FieldType from "@/utils/constants/FieldType.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {DoesFilterPassParams, FilterHandlerName, IFilterDef, ValueFormatterParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import AbsAGGridEditViewColumn from "@/utils/UIConstants/AGGridEditViewColumns/AbsAGGridEditViewColumn.ts";
import AGGridDateTimeFilter from "@/components/inputs/AGGridDateTimeFilter.vue";
import ValueFormatterFactory from "@/utils/UIConstants/ValueFormatterFactory.ts";
import AGGridReferenceFilter from "@/components/inputs/AGGridReferenceFilter.vue";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import DateTimeValueFormatter from "@/utils/UIConstants/AGGridEditViewValueFormatters/DateTimeValueFormatter.ts";
import {NamaAGColDefFilter, NamaAGColDefFilterParams, NamaAGFilter} from "@/utils/presenters/interfaces.ts";
import FieldMetaData = namaTypes.FieldMetaData;

export default class ColumnFiltrationFactory
{
  static fetchFilterData(instance: AbsAGGridEditViewColumn): FilterData | null | undefined {
    let type = instance.columnMetadata.fieldType;
    if (!type)
      return;
    switch (type) {
      case FieldType.Text:
      case FieldType.Link:
      case FieldType.Email:
      case FieldType.BigText:
      case FieldType.PhoneNumber:
      case FieldType.FieldID:
      case FieldType.Measures:
      case FieldType.Color:
      case FieldType.LongToken:
      case FieldType.LatLng:
        return new FilterData("agMultiColumnFilter", {
          filters: [
            {
              filter: "agTextColumnFilter",
              filterParams: {
                textFormatter: (value: string): string | null => value == null ? null : StringUtils.replaceArNumerals(value),
                textMatcher: ColumnFiltrationFactory.textMatcher,
                maxNumConditions: 1,
              }
            },
            this.fetchChildSetColumnFilter(instance)
          ],
        });
      case FieldType.Decimal:
      case FieldType.Long:
      case FieldType.Integer:
        return new FilterData("agNumberColumnFilter", {
          inRangeInclusive: true,
          maxNumConditions: 2,
          // includeBlanksInEquals: true,
          // includeBlanksInGreaterThan: true,
          // includeBlanksInLessThan: true,
          // includeBlanksInNotEqual: true,
          // includeBlanksInRange: true,
        });
      case FieldType.Boolean:
        return new FilterData("agSetColumnFilter",
          this.fetchSetColumnFilterParams((value) => Translator.translateId(value + ''), false));
      case FieldType.Date:
        return new FilterData("agMultiColumnFilter", {
          filters: [
            {
              filter: "agDateColumnFilter",
              filterParams: {
                comparator: (filterValue: Date, cellValue: string) => {
                  if (!cellValue) return -1;
                  const cellDate = new Date(cellValue);
                  if (filterValue.getTime() === cellDate.getTime()) {
                    return 0;
                  }
                  if (cellDate < filterValue) {
                    return -1;
                  }
                  if (cellDate > filterValue) {
                    return 1;
                  }
                  return 0;
                },
                minValidYear: 1800,
                maxValidYear: 2999,
              }
            },
            this.fetchChildSetColumnFilter(instance)
          ],
        });
      case FieldType.DateAndTime:
      case FieldType.Time:
        return new FilterData("agMultiColumnFilter", {
          filters: [
            {
              filter: this.fetchAGDateTimeFilter(),
              filterParams: {
                fieldType: type
              }
            },
            this.fetchChildSetColumnFilter(instance)
          ]
        });
      case FieldType.FeatureID:
      case FieldType.EntityType:
      case FieldType.Enum:
        return new FilterData("agSetColumnFilter",
          this.fetchSetColumnFilterParams((value) => Translator.translate(value, instance.columnMetadata.enumTypeName)));
      case FieldType.Reference:
      case FieldType.Genericreference:
        return new FilterData("agMultiColumnFilter", {
          filters: [
            {
              filter: this.fetchAGReferenceFilter(),
              filterParams: {
                metaData: instance.columnMetadata as FieldMetaData,
              }
            },
            {
              filter: "agSetColumnFilter",
              filterParams: this.fetchSetColumnFilterParams((value) => Translator.translateRef(value),
                true, {keyCreator: (params) => params.value?.id})
            }
          ]
        });
      case FieldType.Binary:
      default:
        return;
    }
  }

  static valueFormatter(instance: AbsAGGridEditViewColumn) {
    let valueFormatter = ValueFormatterFactory.fromFieldType(instance.columnMetadata.fieldType);
    return (params: ValueFormatterParams) =>
      !params.value ? Translator.translateId('blank') : valueFormatter.format(instance.createSpecs(), params.value);
  }

  static fetchSetColumnFilterParams(formatterFn: (value: any) => any, addBlankToNullValues: boolean = true, additionalParams: Record<string, any> = {}): NamaAGColDefFilter['filterParams'] {
    return {
      applyMiniFilterWhileTyping: true,
      valueFormatter: (params: ValueFormatterParams) =>
        !params.value && addBlankToNullValues ? Translator.translateId('blank') : formatterFn(params.value),
      ...additionalParams
    }
  }

  static fetchChildSetColumnFilter(instance: AbsAGGridEditViewColumn): NamaAGColDefFilter {
    const supportTree = this.isSupportTreeList(instance);
    return {
      filter: "agSetColumnFilter",
      filterParams: {
        applyMiniFilterWhileTyping: true,
        valueFormatter: this.valueFormatter(instance),
        treeList: supportTree,
        treeListPathGetter: supportTree ? this.fetchTreeListGetter(instance) : undefined,
      }
    }
  }

  static isSupportTreeList(instance: AbsAGGridEditViewColumn) {
    return [FieldType.Date, FieldType.DateAndTime, FieldType.Time].includes(instance.columnMetadata.fieldType!);
  }

  static fetchTreeListGetter(instance: AbsAGGridEditViewColumn): ((value: any | null) => string[] | null) | undefined {
    if ([FieldType.Date, FieldType.DateAndTime].includes(instance.columnMetadata.fieldType!)) {
      return (date: string) => {
        const momentValue = dayjs(date, ISO_DATE_FORMATS);
        if (!momentValue.isValid()) {
          const valueFormatter = ValueFormatterFactory.fromFieldType(instance.columnMetadata.fieldType);
          return [valueFormatter.format(instance.createSpecs(), date)];
        }
        const result = [momentValue.year().toString(), (momentValue.month() + 1).toString(), momentValue.date().toString()];
        if (instance.columnMetadata.fieldType === FieldType.DateAndTime)
          result.push(Translator.translateId("theHour") + ": " + momentValue.hour().toString());
        return result;
      }
    }
    if (instance.columnMetadata.fieldType === FieldType.Time) {
      return (time: string) => {
        const momentValue = dayjs(time);
        if (!momentValue.isValid()) {
          const valueFormatter = ValueFormatterFactory.fromFieldType(instance.columnMetadata.fieldType);
          return [valueFormatter.format(instance.createSpecs(), time)];
        }
        return [momentValue.hour().toString(), momentValue.minute().toString()];
      }
    }
    return;
  }

  static textMatcher = ({filterOption, value, filterText}): boolean => {
    value = value?.toLowerCase();
    filterText = filterText?.toLowerCase();
    switch (filterOption) {
      case 'contains':
        return value.indexOf(filterText) >= 0
          || value.indexOf(StringUtils.arabicToEnglish(filterText)) >= 0
          || value.indexOf(StringUtils.englishToArabic(filterText)) >= 0;
      case 'notContains':
        return value.indexOf(filterText) < 0
          || value.indexOf(StringUtils.arabicToEnglish(filterText)) < 0
          || value.indexOf(StringUtils.englishToArabic(filterText)) < 0;
      case 'equals':
        return value === filterText
          || value === StringUtils.arabicToEnglish(filterText)
          || value === StringUtils.englishToArabic(filterText);
      case 'notEqual':
        return value != filterText
          || value !== StringUtils.arabicToEnglish(filterText)
          || value !== StringUtils.englishToArabic(filterText);
      case 'startsWith':
        return value.indexOf(filterText) === 0
          || value.indexOf(StringUtils.arabicToEnglish(filterText)) === 0
          || value.indexOf(StringUtils.englishToArabic(filterText)) === 0;
      case 'endsWith':
        const index = value.lastIndexOf(filterText);
        const index2 = value.lastIndexOf(StringUtils.arabicToEnglish(filterText));
        const index3 = value.lastIndexOf(StringUtils.englishToArabic(filterText));
        return (index >= 0 && index + 1 === (value.length - filterText.length))
          || (index2 >= 0 && index2 + 1 === (value.length - filterText.length))
          || (index3 >= 0 && index3 + 1 === (value.length - filterText.length));
      default:
        return true;
    }
  }

  static fetchDateTimeFilterData(): Partial<IFilterDef> {
    return {
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: this.fetchAGDateTimeFilter(),
            filterParams: {
              fieldType: FieldType.DateAndTime
            }
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              applyMiniFilterWhileTyping: true,
              valueFormatter: (params: ValueFormatterParams) =>
                !params.value ? Translator.translateId('blank') : DateTimeValueFormatter.instance().format(undefined, params.value),
              treeList: true,
              treeListPathGetter: (date: string) => {
                const momentValue = dayjs(date, ISO_DATE_FORMATS);
                if (!momentValue.isValid())
                  return [DateTimeValueFormatter.instance().format(undefined, date)];
                return [momentValue.year().toString(), (momentValue.month() + 1).toString(), momentValue.date().toString(),
                  Translator.translateId("theHour") + ": " + momentValue.hour().toString()];
              }
            }
          }
        ]
      }
    }
  }

  public static fetchAGDateTimeFilter(): NamaAGFilter {
    return {
      component: AGGridDateTimeFilter,
      doesFilterPass: (params: DoesFilterPassParams) => {
        const model = params.model;
        if (!model) return true;
        const rowValue = params.handlerParams.getValue(params.node)?.toString();
        if (!rowValue && model.type === 'blank')
          return true;
        if (!rowValue && model.type === 'notBlank')
          return false;
        if (!rowValue)
          return false;

        const rowValueDate = new Date(rowValue);
        const filterValue = model.dateFrom ? new Date(model.dateFrom) : null;
        const toFilterValue = model.dateTo ? new Date(model.dateTo) : null;

        switch (model.type) {
          case 'equals':
            return filterValue ? filterValue.getTime() === rowValueDate.getTime() : false;
          case 'notEqual':
            return filterValue ? filterValue.getTime() !== rowValueDate.getTime() : false;
          case 'before':
            return filterValue ? rowValueDate.getTime() < filterValue.getTime() : false;
          case 'after':
            return filterValue ? rowValueDate.getTime() > filterValue.getTime() : false;
          case 'between':
            return (filterValue && toFilterValue)
              ? filterValue.getTime() <= rowValueDate.getTime() && rowValueDate.getTime() <= toFilterValue.getTime()
              : false;
          case 'blank':
            return !rowValue;
          case 'notBlank':
            return !!rowValue;
          default:
            return true;
        }
      }

    };
  }

  static isReferencesEqual(referenceValue: any, rowValue: any, fieldType: FieldType): boolean {
    if (fieldType === FieldType.Reference)
      return referenceValue?.id === rowValue?.id;
    if (!referenceValue || (!referenceValue.entityType && !referenceValue.id))
      return true;
    if (referenceValue.entityType) {
      if (!referenceValue.id)
        return referenceValue?.entityType === rowValue?.entityType;
      return referenceValue?.id === rowValue?.id;
    }
    return false;
  }

  static fetchAGReferenceFilter(): NamaAGFilter {
    return {
      component: AGGridReferenceFilter,
      doesFilterPass: (params: DoesFilterPassParams) => {
        const model = params.model;
        if (!model)
          return true;
        const rowValue = params.handlerParams.getValue(params.node);
        const referenceValue = model.ref;
        switch (model.type) {
          case 'equals':
            return this.isReferencesEqual(referenceValue, rowValue, model.type);
          case 'notEqual':
            return !this.isReferencesEqual(referenceValue, rowValue, model.type);
          case 'blank':
            return !rowValue;
          case 'notBlank':
            return !!rowValue;
          default:
            return true;
        }
      }
    }
  }
}

export class FilterData
{
  constructor(public filter: FilterHandlerName, public filterParams?: NamaAGColDefFilterParams) {
    this.filter = filter;
    const defaultFilterParams: NamaAGColDefFilterParams = {
      buttons: ['reset']
    }
    if (filterParams)
      this.filterParams = {...defaultFilterParams, ...this.filterParams, ...filterParams};
  }
}
